import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Table, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../config';
import './RelaySetting.css';

function RelaySetting() {
  const settingList = useSelector((state) => state.settingList || {});
  const relayData = useSelector((state) => state.relayData || {});
  const { commPortList = [], relay_protocollist = [] } = settingList;

  const protocolOptions = relay_protocollist.length ? relay_protocollist : ['mrt_relay'];

  const [inputCommPort, setInputCommPort] = useState('RS485-CH1');
  const [inputAddress, setInputAddress] = useState('1');
  const [inputName, setInputName] = useState('릴레이1');
  const [inputProtocol, setInputProtocol] = useState(protocolOptions[0] || 'mrt_relay');
  const [inputIP, setInputIP] = useState('192.168.105.101');
  const [inputPort, setInputPort] = useState('502');

  const [relayList, setRelayList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const isEthernet = inputCommPort === 'ETHERNET';

  useEffect(() => {
    if (protocolOptions.length && !protocolOptions.includes(inputProtocol)) {
      setInputProtocol(protocolOptions[0]);
    }
  }, [protocolOptions, inputProtocol]);

  const validateBeforeSave = ({ commPort, address, name, protocol, ip, port }) => {
    const addrNum = parseInt(address, 10);
    if (isNaN(addrNum) || addrNum < 1 || addrNum > 65535) {
      alert('장비 ID(relay_address)는 1~65535 범위의 정수여야 합니다.');
      return false;
    }
    if (!name?.trim()) {
      alert('릴레이 이름을 입력하세요.');
      return false;
    }
    if (!protocol?.trim()) {
      alert('프로토콜을 선택하세요.');
      return false;
    }
    if (commPort === 'ETHERNET') {
      const portNum = parseInt(port, 10);
      if (isNaN(portNum) || portNum <= 0 || portNum > 65535) {
        alert('이더넷 포트는 1~65535 범위의 정수여야 합니다.');
        return false;
      }
      if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(ip || '')) {
        alert('IP 주소 형식이 올바르지 않습니다. 예) 192.168.105.101');
        return false;
      }
    }
    return true;
  };

  const fetchRelayList = async () => {
    try {
      const res = await axios.get(`${config.backendUrl}/api/get-relay`);
      const data = res.data;
      setRelayList(Array.isArray(data) ? data : (data ? [data] : []));
    } catch (e) {
      console.error('Relay 조회 실패:', e);
    }
  };

  useEffect(() => {
    fetchRelayList();
  }, []);

  const handleAddClick = async () => {
    const payload = {
      name: inputName,
      protocol: inputProtocol,
      commPort: inputCommPort,
      address: Number(inputAddress),
      ip: isEthernet ? inputIP : null,
      port: isEthernet ? Number(inputPort) : null,
    };

    if (!validateBeforeSave({
      commPort: payload.commPort,
      address: payload.address,
      name: payload.name,
      protocol: payload.protocol,
      ip: payload.ip,
      port: payload.port,
    })) return;

    try {
      const resp = await axios.post(`${config.backendUrl}/api/post_relay`, payload);
      if (resp.status === 201 || resp.status === 200) {
        const nextAddr = String(parseInt(inputAddress || '1', 10) + 1);
        const nextName = `${inputName.replace(/[0-9]+$/, '')}${nextAddr}`;
        setInputAddress(nextAddr);
        setInputName(nextName);
        if (isEthernet) setInputIP(inputIP);
        await fetchRelayList();
      } else {
        console.error('Relay 추가 실패:', resp);
      }
    } catch (e) {
      console.error('Relay 추가 중 오류:', e);
      alert('추가 중 오류가 발생했습니다.');
    }
  };

  const handleRowClick = (commPort) => {
    let url = '';
    switch (commPort) {
      case 'RS485-CH1': url = `${config.backendUrl}/RS485-CH1`; break;
      case 'RS485-CH2': url = `${config.backendUrl}/RS485-CH2`; break;
      case 'RS485-CH3': url = `${config.backendUrl}/RS485-CH3`; break;
      case 'ETHERNET': url = `${config.backendUrl}/ETHERNET`; break;
      default: return;
    }
    window.open(url, '_blank');
  };

  const handleEditClick = (item) => {
    setEditItem({ ...item });
    setShowModal(true);
    setIsEditing(true);

    setInputCommPort(item.relay_commPort);
    setInputAddress(String(item.relay_address ?? '1'));
    setInputName(item.relay_name ?? '');
    setInputProtocol(item.relay_protocol ?? protocolOptions[0]);
    setInputIP(item.relay_commPort === 'ETHERNET' ? (item.relay_ethernetIp || '192.168.105.101') : '');
    setInputPort(item.relay_commPort === 'ETHERNET' ? String(item.relay_ethernetPort || '502') : '');
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;

    const payload = {
      name: inputName?.trim(),
      protocol: inputProtocol,
      commPort: inputCommPort,
      address: Number(inputAddress),
      ip: inputCommPort === 'ETHERNET' ? inputIP : null,
      port: inputCommPort === 'ETHERNET' ? Number(inputPort) : null,
    };

    if (!validateBeforeSave({
      commPort: payload.commPort,
      address: payload.address,
      name: payload.name,
      protocol: payload.protocol,
      ip: payload.ip,
      port: payload.port,
    })) return;

    try {
      await axios.put(`${config.backendUrl}/api/update_relay/${editItem.seq}`, payload);
      setShowModal(false);
      setIsEditing(false);
      setEditItem(null);
      await fetchRelayList();
    } catch (e) {
      console.error('Relay 수정 실패:', e);
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteClick = async (seq, name) => {
    if (!window.confirm(`${name} 을(를) 정말로 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.`)) return;
    try {
      await axios.delete(`${config.backendUrl}/api/delete-relay/${seq}`);
      alert(`${name} : 삭제 완료`);
      await fetchRelayList();
    } catch (e) {
      console.error('Relay 삭제 실패:', e);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const seqIndexMap = {};
  relayData.seq?.forEach((s, i) => {
    seqIndexMap[s] = i;
  });

  const portsInList = Array.from(new Set(relayList.map((i) => i.relay_commPort).filter(Boolean)));

  return (
    <Container className="mt-5 relay-setting">
      <Row className="align-items-center justify-content-center mb-3">
        <Col md="2">
          <Form.Label className="text-center w-100">통신 포트</Form.Label>
          <Form.Control
            as="select"
            value={inputCommPort}
            onChange={(e) => setInputCommPort(e.target.value)}
            className="text-center"
          >
            {commPortList.map((port, index) => (
              <option key={index} value={port}>{port}</option>
            ))}
          </Form.Control>
        </Col>

        {isEthernet && (
          <>
            <Col md="2">
              <Form.Label className="text-center w-100">IP 주소</Form.Label>
              <Form.Control
                type="text"
                value={inputIP}
                onChange={(e) => setInputIP(e.target.value)}
                className="text-center"
                placeholder="192.168.105.101"
              />
            </Col>
            <Col md="1">
              <Form.Label className="text-center w-100">포트</Form.Label>
              <Form.Control
                type="text"
                value={inputPort}
                onChange={(e) => setInputPort(e.target.value)}
                className="text-center"
                placeholder="502"
              />
            </Col>
          </>
        )}

        <Col md="1">
          <Form.Label className="text-center w-100">장비 ID</Form.Label>
          <Form.Control
            type="text"
            value={inputAddress}
            onChange={(e) => setInputAddress(e.target.value)}
            className="text-center"
          />
        </Col>

        <Col md="2">
          <Form.Label className="text-center w-100">릴레이 이름</Form.Label>
          <Form.Control
            type="text"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            className="text-center"
          />
        </Col>

        <Col md="2">
          <Form.Label className="text-center w-100">프로토콜 선택</Form.Label>
          <Form.Control
            as="select"
            value={inputProtocol}
            onChange={(e) => setInputProtocol(e.target.value)}
            className="text-center"
          >
            {protocolOptions.map((p, idx) => (
              <option key={idx} value={p}>{p}</option>
            ))}
          </Form.Control>
        </Col>

        <Col md="2" className="text-center">
          <Button variant="info" onClick={handleAddClick} className="mt-4 w-100">
            릴레이 추가
          </Button>
        </Col>
      </Row>

      {portsInList.map((port) => (
        <div key={port}>
          <Table striped bordered hover className="text-center">
            <thead>
              <tr>
                <th>#</th>
                <th onClick={() => handleRowClick(port)} style={{ cursor: 'pointer' }}>{port}</th>
                <th>장비 ID</th>
                <th>릴레이 이름</th>
                <th>프로토콜</th>
                {port === 'ETHERNET' && (<><th>IP 주소</th><th>포트</th></>)}
                <th>송신</th>
                <th>수신</th>
                <th>수정</th>
                <th>삭제</th>
              </tr>
            </thead>
            <tbody>
              {relayList.map((item, index) => {
                if (item.relay_commPort !== port) return null;
                const idx = seqIndexMap[item.seq];
                return (
                  <tr key={item.seq ?? index}>
                    <td>{index + 1}</td>
                    <td>{item.relay_commPort}</td>
                    <td>{item.relay_address}</td>
                    <td>{item.relay_name}</td>
                    <td>{item.relay_protocol}</td>
                    {port === 'ETHERNET' && (
                      <>
                        <td>{item.relay_ethernetIp || '-'}</td>
                        <td>{item.relay_ethernetPort || '-'}</td>
                      </>
                    )}
                    <td>{relayData.sendCount?.[idx] ?? 0}</td>
                    <td>{relayData.receiveCount?.[idx] ?? 0}</td>
                    <td>
                      <Button
                        variant="warning"
                        className="relay-setting__row-btn"
                        onClick={() => handleEditClick(item)}
                      >
                        수정
                      </Button>
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        className="relay-setting__row-btn"
                        onClick={() => handleDeleteClick(item.seq, item.relay_name)}
                      >
                        삭제
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ))}

      <Modal show={showModal} onHide={() => setShowModal(false)} className="custom-modal">
        <Modal.Header closeButton>
          <Modal.Title>릴레이 수정</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col style={{ width: '300px' }}>
                <Form.Group>
                  <Form.Label>통신 포트</Form.Label>
                  <Form.Control
                    as="select"
                    value={inputCommPort}
                    onChange={(e) => setInputCommPort(e.target.value)}
                  >
                    {commPortList.map((port, index) => (
                      <option key={index} value={port}>{port}</option>
                    ))}
                  </Form.Control>
                </Form.Group>

                {inputCommPort === 'ETHERNET' && (
                  <>
                    <Form.Group>
                      <Form.Label>IP 주소</Form.Label>
                      <Form.Control
                        type="text"
                        value={inputIP}
                        onChange={(e) => setInputIP(e.target.value)}
                        placeholder="192.168.105.101"
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>포트</Form.Label>
                      <Form.Control
                        type="text"
                        value={inputPort}
                        onChange={(e) => setInputPort(e.target.value)}
                        placeholder="502"
                      />
                    </Form.Group>
                  </>
                )}

                <Form.Group>
                  <Form.Label>장비 ID(relay_address)</Form.Label>
                  <Form.Control
                    type="text"
                    value={inputAddress}
                    onChange={(e) => setInputAddress(e.target.value)}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>릴레이 이름</Form.Label>
                  <Form.Control
                    type="text"
                    value={inputName}
                    onChange={(e) => setInputName(e.target.value)}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>프로토콜 선택</Form.Label>
                  <Form.Control
                    as="select"
                    value={inputProtocol}
                    onChange={(e) => setInputProtocol(e.target.value)}
                  >
                    {protocolOptions.map((p, idx) => (
                      <option key={idx} value={p}>{p}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              setIsEditing(false);
            }}
          >
            취소
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={!isEditing && !editItem}>
            저장
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default RelaySetting;
